/**
 * Cliente que controla o Web Worker. Não roda no worker; roda na main thread.
 *
 * Uso básico:
 *   const { promise, cancel } = analyzeFilesInWorker(input, { onProgress: setState });
 *   const result = await promise;
 */
import type { DiagnosticInput, DiagnosticResult } from '../core/diagnostic.types';
import type { WorkerOutboundMessage, WorkerStep } from './workerMessages';

/** Estado atual do worker, espelhado na main thread. */
export interface WorkerClientState {
  step: WorkerStep;
  percent: number;
  label: string;
  error: string | null;
}

/** Estado inicial antes de qualquer mensagem ser recebida. */
export const INITIAL_WORKER_STATE: WorkerClientState = {
  step: 'idle',
  percent: 0,
  label: '',
  error: null,
};

export interface AnalyzeOptions {
  onProgress?: (state: WorkerClientState) => void;
}

/**
 * Sobe um worker, envia o input, aguarda result/error.
 * Retorna uma promise que resolve com o DiagnosticResult ou rejeita com erro.
 * O retorno também expõe `cancel()` para interromper a análise a qualquer momento.
 */
export function analyzeFilesInWorker(
  input: DiagnosticInput,
  options?: AnalyzeOptions,
): { promise: Promise<DiagnosticResult>; cancel: () => void } {
  // Vite resolve o URL do worker em tempo de build (sintaxe obrigatória para bundling correto).
  const worker = new Worker(new URL('./analysis.worker.ts', import.meta.url), { type: 'module' });

  let currentState: WorkerClientState = { ...INITIAL_WORKER_STATE };

  let resolveFn: (result: DiagnosticResult) => void;
  let rejectFn: (err: Error) => void;
  let settled = false;

  const promise = new Promise<DiagnosticResult>((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });

  function settle(fn: () => void): void {
    if (settled) return;
    settled = true;
    fn();
    worker.terminate();
  }

  worker.addEventListener('message', (e: MessageEvent<WorkerOutboundMessage>) => {
    const msg = e.data;
    const newState = handleWorkerMessage(currentState, msg);
    currentState = newState;
    options?.onProgress?.(newState);

    if (msg.type === 'result') {
      settle(() => resolveFn(msg.result));
    } else if (msg.type === 'error') {
      settle(() => rejectFn(new Error(msg.message)));
    }
  });

  worker.addEventListener('error', (e: ErrorEvent) => {
    settle(() => rejectFn(new Error(e.message ?? 'Erro interno no worker de análise.')));
  });

  // Envia mensagem de início para o worker
  worker.postMessage({ type: 'start', input });

  function cancel(): void {
    if (settled) return;
    worker.postMessage({ type: 'cancel' });
    settle(() => rejectFn(new Error('Análise cancelada pelo usuário.')));
  }

  return { promise, cancel };
}

/**
 * Aplica uma WorkerOutboundMessage no estado atual e retorna o novo estado.
 * Função pura — útil em testes e em redutores de estado da UI.
 */
export function handleWorkerMessage(
  state: WorkerClientState,
  message: WorkerOutboundMessage,
): WorkerClientState {
  switch (message.type) {
    case 'progress':
      return {
        ...state,
        step: message.step,
        percent: message.percent,
        label: message.label,
        error: null,
      };
    case 'result':
      return {
        ...state,
        step: 'done',
        percent: 100,
        label: 'Pronto',
        error: null,
      };
    case 'error':
      return {
        ...state,
        step: 'error',
        error: message.message,
      };
  }
}
